import React, { useEffect, useMemo, useRef, useState } from "react";
import { AppAvatar } from "../components/AppAvatar";
import { useAuth } from "../context/AuthContext";
import { api, formatApiDetail, postMultipart } from "../lib/api";
import {
  readStudioPrefs,
  writeStudioPrefs,
} from "../lib/studioPrefs";
import { cn } from "../lib/utils";
import { toast } from "sonner";

const SECTIONS = [
  { id: "account", label: "Account" },
  { id: "access", label: "Access" },
  { id: "studio", label: "Studio" },
  { id: "audit", label: "Audit" },
];

function Field({ label, children }) {
  return (
    <div>
      <label className="label-eyebrow mb-2 block">{label}</label>
      {children}
    </div>
  );
}

function ToggleRow({ label, checked, onChange, testId }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className="flex w-full items-center justify-between gap-4 rounded-[var(--t-event-pill-radius)] px-1 py-2.5 text-left hover:bg-black/[0.02]"
      data-testid={testId}
    >
      <span className="text-sm text-[var(--t-text)]">{label}</span>
      <span className={cn("t-pill shrink-0", checked ? "t-pill-accent" : "t-pill-meta")}>
        {checked ? "On" : "Off"}
      </span>
    </button>
  );
}

function StatusPill({ ok, label }) {
  return <span className={cn("t-pill", ok ? "t-pill-accent" : "t-pill-meta")}>{label}</span>;
}

function Row({ title, trailing, children }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2 text-sm text-[var(--t-text)]">{title}</div>
        {children}
      </div>
      {trailing}
    </div>
  );
}

export default function AdminProfilePage() {
  const { user, refresh, logout } = useAuth();
  const [section, setSection] = useState("account");
  const [saving, setSaving] = useState(false);
  const [localPreview, setLocalPreview] = useState(null);
  const [avatarBusy, setAvatarBusy] = useState(false);
  const avatarInputRef = useRef(null);
  const [googleBusy, setGoogleBusy] = useState(false);
  const [passwordBusy, setPasswordBusy] = useState(false);
  const [twoFaBusy, setTwoFaBusy] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [twoFaCode, setTwoFaCode] = useState("");
  const [twoFaPending, setTwoFaPending] = useState(false);
  const [passwords, setPasswords] = useState({ current: "", next: "", confirm: "" });

  const [form, setForm] = useState(() => ({
    name: user?.name || "",
    email: user?.email || "",
    phone: user?.phone || "",
  }));

  const prefs = useMemo(() => readStudioPrefs(), []);
  const [alerts, setAlerts] = useState(prefs.alerts);
  const [workspace, setWorkspace] = useState(prefs.workspace);
  const [auditQuery, setAuditQuery] = useState("");
  const [auditDebounced, setAuditDebounced] = useState("");
  const [auditItems, setAuditItems] = useState([]);
  const [auditBusy, setAuditBusy] = useState(false);

  useEffect(() => {
    if (!user) return;
    setForm({
      name: user.name || "",
      email: user.email || "",
      phone: user.phone || "",
    });
  }, [user?.id, user?.name, user?.email, user?.phone]);

  useEffect(() => {
    return () => {
      if (localPreview) URL.revokeObjectURL(localPreview);
    };
  }, [localPreview]);

  useEffect(() => {
    if (section !== "audit") {
      setAuditQuery("");
      setAuditDebounced("");
      setAuditItems([]);
      return undefined;
    }
    const t = window.setTimeout(() => setAuditDebounced(auditQuery.trim()), 250);
    return () => window.clearTimeout(t);
  }, [section, auditQuery]);

  useEffect(() => {
    if (section !== "audit") return undefined;
    let cancelled = false;
    (async () => {
      setAuditBusy(true);
      try {
        const params = { limit: 200 };
        if (auditDebounced) params.q = auditDebounced;
        const { data } = await api.get("/admin/audit-logs", { params });
        if (!cancelled) setAuditItems(data.items || []);
      } catch (err) {
        if (!cancelled) {
          toast.error(formatApiDetail(err.response?.data?.detail, "Could not load audit log"));
        }
      } finally {
        if (!cancelled) setAuditBusy(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [section, auditDebounced]);

  const avatarSrc = useMemo(
    () => localPreview || user?.avatar_url || null,
    [localPreview, user?.avatar_url],
  );
  const googleConnected = Boolean(user?.google_calendar_connected);
  const twoFaEnabled = Boolean(user?.two_factor_enabled);
  const identity = {
    name: form.name || user?.name || "—",
    email: form.email || user?.email || "",
  };

  const saveProfile = async (e) => {
    e?.preventDefault?.();
    setSaving(true);
    try {
      await api.patch("/auth/me", {
        name: form.name,
        email: form.email,
        phone: form.phone,
      });
      await refresh();
      toast.success("Saved");
    } catch (err) {
      toast.error(formatApiDetail(err.response?.data?.detail, "Could not save"));
    } finally {
      setSaving(false);
    }
  };

  const uploadAvatar = async (file) => {
    if (!file) return;
    if (localPreview) URL.revokeObjectURL(localPreview);
    setLocalPreview(URL.createObjectURL(file));
    setAvatarBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await postMultipart("/auth/me/avatar", fd);
      localStorage.setItem("taistu_avatar_bust", String(Date.now()));
      await refresh();
      toast.success("Photo updated");
    } catch (err) {
      toast.error(formatApiDetail(err.response?.data?.detail, "Could not upload photo"));
    } finally {
      setAvatarBusy(false);
      if (avatarInputRef.current) avatarInputRef.current.value = "";
    }
  };

  const connectGoogle = async () => {
    setGoogleBusy(true);
    try {
      const { data } = await api.get("/auth/google/url");
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiDetail(e.response?.data?.detail, "Could not start Google connection"));
      setGoogleBusy(false);
    }
  };

  const disconnectGoogle = async () => {
    setGoogleBusy(true);
    try {
      await api.delete("/auth/google");
      await refresh();
      toast.success("Disconnected");
    } catch (e) {
      toast.error(formatApiDetail(e.response?.data?.detail, "Could not disconnect Google"));
    } finally {
      setGoogleBusy(false);
    }
  };

  const startTwoFa = async () => {
    setTwoFaBusy(true);
    try {
      await api.post("/auth/me/2fa/start", { method: "email" });
      setTwoFaPending(true);
      setTwoFaCode("");
      toast.success("Code sent");
    } catch (e) {
      toast.error(formatApiDetail(e.response?.data?.detail, "Could not start 2FA"));
    } finally {
      setTwoFaBusy(false);
    }
  };

  const verifyTwoFa = async () => {
    if (!twoFaCode.trim()) return;
    setTwoFaBusy(true);
    try {
      await api.post("/auth/me/2fa/verify", { method: "email", code: twoFaCode.trim() });
      await refresh();
      setTwoFaPending(false);
      setTwoFaCode("");
      toast.success("2FA enabled");
    } catch (e) {
      toast.error(formatApiDetail(e.response?.data?.detail, "Invalid or expired code"));
    } finally {
      setTwoFaBusy(false);
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (passwords.next.length < 8) {
      toast.error("New password must be at least 8 characters");
      return;
    }
    if (passwords.next !== passwords.confirm) {
      toast.error("New passwords do not match");
      return;
    }
    setPasswordBusy(true);
    try {
      await api.post("/auth/me/password", {
        current_password: passwords.current,
        new_password: passwords.next,
      });
      setPasswords({ current: "", next: "", confirm: "" });
      setShowPasswordForm(false);
      toast.success("Password updated — sign in again");
      logout();
    } catch (err) {
      toast.error(formatApiDetail(err.response?.data?.detail, "Could not update password"));
    } finally {
      setPasswordBusy(false);
    }
  };

  const saveStudio = () => {
    writeStudioPrefs({ alerts, workspace });
    toast.success("Saved");
  };

  return (
    <div className="min-h-0 max-w-full overflow-x-clip p-4 sm:p-6 md:p-12 t-fade-up" data-testid="admin-profile-page">
      <header className="mb-8">
        <div className="label-eyebrow mb-2">Account</div>
        <h1 className="font-heading text-3xl tracking-tight !font-extralight md:text-4xl">Settings</h1>
      </header>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[12rem_minmax(0,1fr)] lg:items-start">
        <aside className="neo-panel sticky top-4 space-y-1 p-2" data-testid="profile-settings-rail">
          {SECTIONS.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => setSection(s.id)}
              className={cn(
                "flex w-full items-center rounded-[var(--t-event-pill-radius)] px-3 py-2.5 text-left text-[10px] font-normal uppercase tracking-[0.16em] transition-colors",
                section === s.id
                  ? "border border-[var(--t-accent)] bg-[var(--t-accent)]/5 text-[var(--t-accent)]"
                  : "text-[var(--t-meta)] hover:bg-black/[0.04] hover:text-[var(--t-text)]",
              )}
              data-testid={`profile-rail-${s.id}`}
            >
              {s.label}
            </button>
          ))}
        </aside>

        <div className="neo-panel min-w-0 p-5 sm:p-8">
          {section === "account" ? (
            <form onSubmit={saveProfile} className="space-y-5" data-testid="profile-section-account">
              <div className="flex items-center gap-4">
                <AppAvatar name={identity.name} src={avatarSrc} size={64} />
                <div className="min-w-0 flex-1">
                  <div className="font-heading truncate text-xl tracking-tight !font-extralight">
                    {identity.name}
                  </div>
                  <p className="truncate text-sm text-[var(--t-meta)]">{identity.email}</p>
                  <label
                    className={cn(
                      "mt-2 inline-flex cursor-pointer",
                      avatarBusy && "pointer-events-none opacity-50",
                    )}
                  >
                    <input
                      ref={avatarInputRef}
                      type="file"
                      accept="image/png,image/jpeg,image/jpg,image/webp,image/gif"
                      className="sr-only"
                      disabled={avatarBusy}
                      onChange={(e) => uploadAvatar(e.target.files?.[0])}
                      data-testid="admin-profile-avatar-input"
                    />
                    <span className="label-eyebrow text-[var(--t-meta)] hover:text-[var(--t-accent)]">
                      {avatarBusy ? "Uploading…" : avatarSrc ? "Replace photo" : "Add photo"}
                    </span>
                  </label>
                </div>
              </div>

              <Field label="Name">
                <input
                  className="t-input !rounded-[var(--t-event-pill-radius)] !bg-white"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                  data-testid="profile-name"
                />
              </Field>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field label="Email">
                  <input
                    type="email"
                    className="t-input !rounded-[var(--t-event-pill-radius)] !bg-white"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    required
                    data-testid="profile-email"
                  />
                </Field>
                <Field label="Phone">
                  <input
                    className="t-input !rounded-[var(--t-event-pill-radius)] !bg-white"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    placeholder="+1…"
                    data-testid="profile-phone"
                  />
                </Field>
              </div>
              <div className="flex justify-end border-t border-[var(--t-border)] pt-5">
                <button
                  type="submit"
                  className="t-btn-primary !rounded-[var(--t-event-pill-radius)] min-h-[44px] px-6"
                  disabled={saving}
                  data-testid="profile-save"
                >
                  {saving ? "Saving…" : "Save"}
                </button>
              </div>
            </form>
          ) : null}

          {section === "access" ? (
            <div className="space-y-5" data-testid="profile-section-access">
              <Row
                title="Password"
                trailing={
                  <button
                    type="button"
                    className="t-btn-secondary !rounded-[var(--t-event-pill-radius)] min-h-[40px] px-4"
                    onClick={() => setShowPasswordForm((v) => !v)}
                    data-testid="profile-password-toggle"
                  >
                    {showPasswordForm ? "Cancel" : "Update"}
                  </button>
                }
              />
              {showPasswordForm ? (
                <form onSubmit={changePassword} className="neo-well space-y-3 p-4">
                  <Field label="Current">
                    <input
                      type="password"
                      autoComplete="current-password"
                      className="t-input !rounded-[var(--t-event-pill-radius)] !bg-white"
                      value={passwords.current}
                      onChange={(e) => setPasswords({ ...passwords, current: e.target.value })}
                      required
                      data-testid="profile-password-current"
                    />
                  </Field>
                  <Field label="New">
                    <input
                      type="password"
                      autoComplete="new-password"
                      className="t-input !rounded-[var(--t-event-pill-radius)] !bg-white"
                      value={passwords.next}
                      onChange={(e) => setPasswords({ ...passwords, next: e.target.value })}
                      required
                      minLength={8}
                      data-testid="profile-password-next"
                    />
                  </Field>
                  <Field label="Confirm">
                    <input
                      type="password"
                      autoComplete="new-password"
                      className="t-input !rounded-[var(--t-event-pill-radius)] !bg-white"
                      value={passwords.confirm}
                      onChange={(e) => setPasswords({ ...passwords, confirm: e.target.value })}
                      required
                      minLength={8}
                      data-testid="profile-password-confirm"
                    />
                  </Field>
                  <button
                    type="submit"
                    className="t-btn-primary !rounded-[var(--t-event-pill-radius)] min-h-[44px] px-6"
                    disabled={passwordBusy}
                    data-testid="profile-password-save"
                  >
                    {passwordBusy ? "Updating…" : "Save password"}
                  </button>
                </form>
              ) : null}

              <div className="border-t border-[var(--t-border)] pt-5">
                <Row
                  title={
                    <>
                      Two-factor
                      <StatusPill ok={twoFaEnabled} label={twoFaEnabled ? "On" : "Off"} />
                    </>
                  }
                  trailing={
                    !twoFaEnabled ? (
                      <button
                        type="button"
                        className="t-btn-secondary !rounded-[var(--t-event-pill-radius)] min-h-[40px] px-4"
                        onClick={startTwoFa}
                        disabled={twoFaBusy || twoFaPending}
                        data-testid="profile-2fa-start"
                      >
                        {twoFaBusy ? "Sending…" : "Enable"}
                      </button>
                    ) : null
                  }
                />
                {twoFaPending && !twoFaEnabled ? (
                  <div className="neo-well mt-3 space-y-3 p-4">
                    <Field label="Code">
                      <input
                        className="t-input !rounded-[var(--t-event-pill-radius)] !bg-white tabular-nums tracking-[0.2em]"
                        value={twoFaCode}
                        onChange={(e) => setTwoFaCode(e.target.value)}
                        placeholder="000000"
                        maxLength={6}
                        data-testid="profile-2fa-code"
                      />
                    </Field>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        className="t-btn-primary !rounded-[var(--t-event-pill-radius)] min-h-[40px] px-5"
                        onClick={verifyTwoFa}
                        disabled={twoFaBusy || twoFaCode.trim().length < 6}
                        data-testid="profile-2fa-verify"
                      >
                        {twoFaBusy ? "Verifying…" : "Verify"}
                      </button>
                      <button
                        type="button"
                        className="label-eyebrow text-[var(--t-meta)] hover:text-[var(--t-accent)]"
                        onClick={startTwoFa}
                        disabled={twoFaBusy}
                      >
                        Resend
                      </button>
                    </div>
                  </div>
                ) : null}
              </div>

              <div className="border-t border-[var(--t-border)] pt-5">
                <Row
                  title={
                    <>
                      Google Calendar
                      <StatusPill
                        ok={googleConnected}
                        label={googleConnected ? "Connected" : "Off"}
                      />
                    </>
                  }
                  trailing={
                    !googleConnected ? (
                      <button
                        type="button"
                        className="t-btn-secondary !rounded-[var(--t-event-pill-radius)] min-h-[40px] px-4"
                        onClick={connectGoogle}
                        disabled={googleBusy}
                        data-testid="profile-connect-google"
                      >
                        {googleBusy ? "…" : "Connect"}
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="label-eyebrow text-[var(--t-meta)] hover:text-[var(--t-accent)]"
                        onClick={disconnectGoogle}
                        disabled={googleBusy}
                        data-testid="profile-disconnect-google"
                      >
                        Disconnect
                      </button>
                    )
                  }
                />
              </div>

              <div className="border-t border-[var(--t-border)] pt-5">
                <Row
                  title="Session"
                  trailing={
                    <button
                      type="button"
                      className="label-eyebrow text-[var(--t-meta)] hover:text-[var(--t-accent)]"
                      onClick={logout}
                      data-testid="profile-sign-out"
                    >
                      Sign out
                    </button>
                  }
                />
              </div>
            </div>
          ) : null}

          {section === "audit" ? (
            <div className="flex flex-col" data-testid="profile-section-audit">
              <header className="mb-4">
                <div className="label-eyebrow mb-2">Audit</div>
                <h2 className="font-heading text-2xl tracking-tight !font-extralight">
                  Security log
                </h2>
              </header>
              <input
                type="search"
                className="t-input mb-4 !rounded-[var(--t-event-pill-radius)] !bg-white"
                value={auditQuery}
                onChange={(e) => setAuditQuery(e.target.value)}
                placeholder="Search email, action, IP…"
                data-testid="profile-audit-search"
              />
              <div className="min-h-[300px] flex-1 overflow-y-auto rounded-[var(--t-event-pill-radius)] border border-[var(--t-border)] bg-white px-4">
                {auditBusy ? (
                  <p className="t-meta py-6">Loading…</p>
                ) : auditItems.length === 0 ? (
                  <p className="py-6 text-sm text-[var(--t-meta)]">No matching events.</p>
                ) : (
                  <ul className="divide-y divide-[var(--t-border)]" data-testid="profile-audit-list">
                    {auditItems.map((row) => (
                      <li key={row.id} className="py-3 text-sm">
                        <div className="flex flex-wrap items-baseline justify-between gap-2">
                          <span className="font-mono text-[11px] text-[var(--t-accent)]">{row.action}</span>
                          <span className="text-[11px] text-[var(--t-meta)]">
                            {row.ts ? new Date(row.ts).toLocaleString() : ""}
                          </span>
                        </div>
                        <p className="mt-0.5 text-[11px] text-[var(--t-meta)]">
                          {[row.actor_email || "—", row.ip, row.resource_type, row.resource_id]
                            .filter(Boolean)
                            .join(" · ")}
                        </p>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          ) : null}

          {section === "studio" ? (
            <div className="space-y-6" data-testid="profile-section-studio">
              <div>
                <div className="label-eyebrow mb-3">Alerts</div>
                <div className="space-y-0.5">
                  <ToggleRow
                    label="Invoice reminders"
                    checked={alerts.invoice_reminders}
                    onChange={(v) => setAlerts({ ...alerts, invoice_reminders: v })}
                    testId="profile-alert-invoice"
                  />
                  <ToggleRow
                    label="Scope responses"
                    checked={alerts.scope_responses}
                    onChange={(v) => setAlerts({ ...alerts, scope_responses: v })}
                    testId="profile-alert-scope"
                  />
                  <ToggleRow
                    label="Packet delivery"
                    checked={alerts.packet_sends}
                    onChange={(v) => setAlerts({ ...alerts, packet_sends: v })}
                    testId="profile-alert-packet"
                  />
                  <ToggleRow
                    label="Stale invoice stage"
                    checked={alerts.outreach_stale}
                    onChange={(v) => setAlerts({ ...alerts, outreach_stale: v })}
                    testId="profile-alert-stale"
                  />
                  <ToggleRow
                    label="BLU finalize"
                    checked={alerts.blu_finalize}
                    onChange={(v) => setAlerts({ ...alerts, blu_finalize: v })}
                    testId="profile-alert-blu"
                  />
                </div>
              </div>

              <div className="border-t border-[var(--t-border)] pt-5">
                <div className="label-eyebrow mb-3">Defaults</div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <Field label="Timezone">
                    <select
                      className="t-select h-11 w-full appearance-none !rounded-[var(--t-event-pill-radius)] !bg-white !px-4"
                      value={workspace.timezone}
                      onChange={(e) => setWorkspace({ ...workspace, timezone: e.target.value })}
                      data-testid="profile-timezone"
                    >
                      <option value="America/New_York">Eastern</option>
                      <option value="America/Chicago">Central</option>
                      <option value="America/Denver">Mountain</option>
                      <option value="America/Los_Angeles">Pacific</option>
                      <option value="UTC">UTC</option>
                    </select>
                  </Field>
                  <Field label="Date format">
                    <select
                      className="t-select h-11 w-full appearance-none !rounded-[var(--t-event-pill-radius)] !bg-white !px-4"
                      value={workspace.dateFormat}
                      onChange={(e) => setWorkspace({ ...workspace, dateFormat: e.target.value })}
                      data-testid="profile-date-format"
                    >
                      <option value="MMM d, yyyy">Apr 7, 2026</option>
                      <option value="yyyy-MM-dd">2026-04-07</option>
                      <option value="dd/MM/yyyy">07/04/2026</option>
                    </select>
                  </Field>
                </div>
                <div className="mt-4">
                  <Field label="Surface">
                    <div className="flex flex-wrap gap-2">
                      {[
                        ["merge", "Merge"],
                        ["bloom", "Bloom"],
                        ["signal", "Signal"],
                      ].map(([value, label]) => (
                        <button
                          key={value}
                          type="button"
                          onClick={() => setWorkspace({ ...workspace, neo: value })}
                          className={cn(
                            "t-pill inline-flex cursor-pointer border bg-transparent font-[inherit]",
                            workspace.neo === value ? "t-pill-accent" : "t-pill-meta",
                          )}
                          data-testid={`profile-neo-${value}`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </Field>
                </div>
              </div>

              <div className="flex justify-end border-t border-[var(--t-border)] pt-5">
                <button
                  type="button"
                  className="t-btn-primary !rounded-[var(--t-event-pill-radius)] min-h-[44px] px-6"
                  onClick={saveStudio}
                  data-testid="profile-workspace-save"
                >
                  Save
                </button>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
